import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';
import { useProducts } from '../context/ProductsContext';
import { shopPriceLabel, minListPrice } from '../utils/price';
import './Shop.css';

const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.35 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const categories = ['All', 'Subscriptions', 'Wrapped Bouquets', 'Arranged Bouquets', 'Seasonal Specials', 'Dahlia Tubers', 'Weddings'];

const Shop = () => {
  const { products, loading } = useProducts();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCategory = searchParams.get('category');
  const initialCategory = categories.includes(urlCategory) ? urlCategory : 'All';
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [filterOpen, setFilterOpen] = useState(false);

  const hasPanelFilters = Boolean(searchQuery.trim()) || sortBy !== 'default';

  useEffect(() => {
    if (urlCategory && categories.includes(urlCategory) && urlCategory !== activeCategory) {
      setActiveCategory(urlCategory);
    }
  }, [urlCategory, activeCategory]);

  const selectCategory = (cat) => {
    setActiveCategory(cat);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (cat === 'All') {
        next.delete('category');
      } else {
        next.set('category', cat);
      }
      return next;
    }, { replace: true });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSortBy('default');
    setActiveCategory('All');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('category');
      return next;
    }, { replace: true });
  };

  const clearPanelFilters = () => {
    setSearchQuery('');
    setSortBy('default');
  };

  useEffect(() => {
    if (!filterOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setFilterOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [filterOpen]);

  const filtered = useMemo(() => {
    let list =
      activeCategory === 'All'
        ? products
        : products.filter((p) => p.category === activeCategory);
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const hay = [p.name, p.shortDesc, p.category, p.slug]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }
    const copy = [...list];
    switch (sortBy) {
      case 'name':
        copy.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'price-asc':
        copy.sort((a, b) => minListPrice(a) - minListPrice(b));
        break;
      case 'price-desc':
        copy.sort((a, b) => minListPrice(b) - minListPrice(a));
        break;
      default:
        break;
    }
    return copy;
  }, [products, activeCategory, searchQuery, sortBy]);

  return (
    <motion.main
      className="shop"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Page Header */}
      <section className="page-header">
        <div className="page-header__bg">
          <img
            src="/background.png"
            alt="Cory Blossom flower farm"
          />
          <div className="page-header__overlay" />
        </div>
        <div className="page-header__content">
          <span className="script-font page-header__eyebrow">Finger Lakes Fresh</span>
          <h1>Shop</h1>
          <p className="page-header__sub">
            Farm-fresh flowers grown in the heart of New York's Finger Lakes region
          </p>
        </div>
      </section>

      {/* Filters & Products */}
      <section className="shop__content">
        <div className="container">
          <AnimatedSection className="shop__filters-bar">
            <div className="shop__filter-chips">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className={`shop__filter-btn ${activeCategory === cat ? 'shop__filter-btn--active' : ''}`}
                  onClick={() => selectCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`shop__filter-trigger ${hasPanelFilters ? 'shop__filter-trigger--active' : ''}`}
              onClick={() => setFilterOpen(true)}
              aria-expanded={filterOpen}
              aria-controls="shop-filter-panel"
              aria-haspopup="dialog"
            >
              <span className="shop__filter-trigger-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
                </svg>
              </span>
              {hasPanelFilters ? <span className="shop__filter-trigger-dot" aria-hidden="true" /> : null}
              <span className="shop__sr-only">Open search and sort</span>
            </button>
          </AnimatedSection>

          {filterOpen &&
            createPortal(
              <>
                <div
                  className="shop__filter-backdrop"
                  onClick={() => setFilterOpen(false)}
                  aria-hidden="true"
                />
                <div
                  id="shop-filter-panel"
                  className="shop__filter-panel"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="shop-filter-title"
                >
                  <div className="shop__filter-panel-header">
                    <h2 id="shop-filter-title" className="shop__filter-panel-title">
                      Search &amp; sort
                    </h2>
                    <button
                      type="button"
                      className="shop__filter-panel-close"
                      onClick={() => setFilterOpen(false)}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                  <div className="shop__filter-panel-body">
                    <div className="shop__search-wrap">
                      <label htmlFor="shop-search" className="shop__filter-field-label">
                        Search
                      </label>
                      <div className="shop__search-inner">
                        <span className="shop__search-icon" aria-hidden="true">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <path d="M21 21l-4.35-4.35" />
                          </svg>
                        </span>
                        <input
                          id="shop-search"
                          type="search"
                          className="shop__search"
                          placeholder="Name, category…"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          autoComplete="off"
                        />
                        {searchQuery.trim() ? (
                          <button
                            type="button"
                            className="shop__search-clear"
                            onClick={() => setSearchQuery('')}
                            aria-label="Clear search"
                          >
                            ×
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <div className="shop__sort-wrap">
                      <label htmlFor="shop-sort" className="shop__filter-field-label">
                        Sort by
                      </label>
                      <select
                        id="shop-sort"
                        className="shop__sort"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                      >
                        <option value="default">Featured</option>
                        <option value="name">Name (A–Z)</option>
                        <option value="price-asc">Price (low to high)</option>
                        <option value="price-desc">Price (high to low)</option>
                      </select>
                    </div>
                    {hasPanelFilters ? (
                      <button type="button" className="btn btn-outline shop__filter-panel-reset" onClick={clearPanelFilters}>
                        Reset search &amp; sort
                      </button>
                    ) : null}
                  </div>
                </div>
              </>,
              document.body
            )}

          {!loading && filtered.length > 0 && (
            <p className="shop__results-meta">
              Showing {filtered.length}
              {filtered.length !== products.length ? ` of ${products.length}` : ''} product
              {filtered.length !== 1 ? 's' : ''}
            </p>
          )}

          {loading && (
            <p style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
              Loading products...
            </p>
          )}
          {!loading && filtered.length === 0 && (
            <div className="shop__empty">
              <p>No products match your filters.</p>
              <button type="button" className="btn btn-outline shop__empty-clear" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="shop__grid">
              {filtered.map((product, i) => (
                <AnimatedSection key={product.id} delay={Math.min(i * 0.04, 0.35)}>
                  <Link to={`/shop/${product.slug}`} className="product-card-link">
                    <div className="product-card">
                      <div className="product-card__img">
                        <img src={product.img} alt={product.name} loading="lazy" />
                        <div className="product-card__hover-overlay">
                          <span className="btn btn-white">View Details</span>
                        </div>
                        {product.badge && (
                          <span className="product-card__badge">{product.badge}</span>
                        )}
                      </div>
                      <div className="product-card__info">
                        <span className="product-card__category">{product.category}</span>
                        <h3>{product.name}</h3>
                        <p className="product-card__short-desc">{product.shortDesc}</p>
                        <div className="product-card__price-row">
                          <span className={`product-card__price ${product.category === 'Weddings' ? 'product-card__price--inquire' : ''}`}>
                            {product.category === 'Weddings' ? 'Contact to inquire' : shopPriceLabel(product)}
                          </span>
                          <span className="product-card__view-link">
                            {product.category === 'Weddings' ? 'Details & inquiry' : 'View'}
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M5 12h14M12 5l7 7-7 7" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          )}
        </div>
      </section>
    </motion.main>
  );
};

export default Shop;
